import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@dental-ui/shared/i18n';

export interface ToothData {
  id: number;
  x: number;
  y: number;
  measurements: [number, number, number];
}

@Component({
  selector: 'lib-tooth-chart',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './tooth-chart.component.html',
  styleUrl: './tooth-chart.component.scss',
})
export class ToothChartComponent {
  @Input() selectedToothIds: number[] = [];
  @Input() toothColors: Map<number, string> = new Map();
  @Input() selectionColor = '#1976D2';
  @Input() isJawService = false;
  @Input() upperJawSelected = false;
  @Input() lowerJawSelected = false;
  @Input() readOnly = false;
  @Input() showNumbers = true;
  @Input() showQuadrants = true;

  @Output() toothClick = new EventEmitter<number>();
  @Output() jawClick = new EventEmitter<'upper' | 'lower'>();

  readonly upperTeeth = signal<ToothData[]>(this.generateUpperTeethData());
  readonly lowerTeeth = signal<ToothData[]>(this.generateLowerTeethData());

  isToothSelected(toothId: number): boolean {
    if (this.isJawService) {
      const isUpper = toothId > 10 && toothId < 30;
      return isUpper ? this.upperJawSelected : this.lowerJawSelected;
    }
    return this.selectedToothIds.includes(toothId);
  }

  getToothColor(toothId: number): string {
    if (this.isToothSelected(toothId)) {
      return this.selectionColor;
    }
    return this.toothColors.get(toothId) || this.selectionColor;
  }

  getToothFill(toothId: number): string {
    return this.isToothSelected(toothId) || this.toothColors.has(toothId)
      ? this.getToothColor(toothId)
      : 'var(--text-muted)';
  }

  onToothClick(id: number): void {
    if (this.readOnly) return;
    this.toothClick.emit(id);
  }

  onJawClick(side: 'upper' | 'lower'): void {
    if (this.readOnly) return;
    this.jawClick.emit(side);
  }

  private generateUpperTeethData(): ToothData[] {
    const teeth: ToothData[] = [];
    const startX = 80;
    const spacing = 28;
    const baseY = 120;
    const upperToothNumbers = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];

    for (let i = 0; i < upperToothNumbers.length; i++) {
      teeth.push({
        id: upperToothNumbers[i],
        x: startX + i * spacing,
        y: baseY,
        measurements: [0, 0, 0],
      });
    }
    return teeth;
  }

  private generateLowerTeethData(): ToothData[] {
    const teeth: ToothData[] = [];
    const startX = 80;
    const spacing = 28;
    const baseY = 180;
    const lowerToothNumbers = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

    for (let i = 0; i < lowerToothNumbers.length; i++) {
      teeth.push({
        id: lowerToothNumbers[i],
        x: startX + i * spacing,
        y: baseY,
        measurements: [0, 0, 0],
      });
    }
    return teeth;
  }
}
